import { editorStateSchema, type EditorState } from '@xsscool/shared';

export const STORAGE_KEY = 'xsscool:state';
export const TAB_STORAGE_KEY = 'xsscool:tab';
export const DOMAIN_STORAGE_KEY = 'xsscool:domain';

export interface PersistedDomain {
  base: string;
  sub: string;
}

export interface HydratedShare {
  id: string;
  name: string | null;
  owned: boolean;
  updatedAt: number;
}

export function loadDomainFromStorage(): PersistedDomain | null {
  try {
    const v = localStorage.getItem(DOMAIN_STORAGE_KEY);
    if (!v) return null;
    const parsed = JSON.parse(v);
    if (parsed && typeof parsed.base === 'string' && typeof parsed.sub === 'string') {
      return { base: parsed.base, sub: parsed.sub };
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function saveDomainToStorage(value: PersistedDomain): void {
  try {
    localStorage.setItem(DOMAIN_STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

const VALID_TABS = new Set(['body', 'headers', 'captures']);
export type PersistedTab = 'body' | 'headers' | 'captures';

export function loadTabFromStorage(): PersistedTab | null {
  try {
    const v = localStorage.getItem(TAB_STORAGE_KEY);
    if (v && VALID_TABS.has(v)) return v as PersistedTab;
  } catch {
    /* ignore */
  }
  return null;
}

export function saveTabToStorage(tab: string): void {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    /* ignore */
  }
}

function readJsonScript(id: string): unknown {
  try {
    const node = document.getElementById(id);
    if (node?.textContent) return JSON.parse(node.textContent);
  } catch {
    /* ignore */
  }
  return null;
}

// Server-injected share metadata (/e/<id>). Null on a plain editor load.
export function loadHydratedShare(): HydratedShare | null {
  if (typeof document === 'undefined') return null;
  const meta = readJsonScript('__xsscool_share') as Partial<HydratedShare> | null;
  if (!meta || typeof meta.id !== 'string') return null;
  return {
    id: meta.id,
    name: typeof meta.name === 'string' ? meta.name : null,
    owned: meta.owned === true,
    updatedAt: typeof meta.updatedAt === 'number' ? meta.updatedAt : 0,
  };
}

// Server-injected state wins over localStorage: opening /e/<id> must show
// the share, not whatever scratch was left behind.
export function loadFromStorage(): Partial<EditorState> {
  if (typeof document !== 'undefined') {
    const hydrated = readJsonScript('__xsscool_state');
    if (hydrated) {
      const result = editorStateSchema.safeParse(hydrated);
      if (result.success) return result.data;
    }
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const result = editorStateSchema.safeParse(JSON.parse(raw));
    if (result.success) return result.data;
  } catch {
    /* ignore */
  }
  return {};
}

export function saveToStorage(state: EditorState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}
